/* Mirrors what the Rust API serialises. Kept hand-written and small: the API
   is one bootstrap payload plus a websocket event stream, not a big surface. */

export interface Me {
  name: string;
  admin: boolean;
}

export interface Voice {
  voice_id: string;
  name: string;
  category: string | null;
  description: string;
  labels: Record<string, string>;
  preview_url: string | null;
  settings: Record<string, unknown>;
}

export interface Model {
  model_id: string;
  name: string | null;
  description: string;
  cost_factor: number;
  max_chars: number | null;
  languages: string[];
}

export type Status =
  | "queued"
  | "generating"
  | "ready"
  | "played"
  | "failed"
  | "skipped"
  /* A preview: generated for its sender's ears and not sent to the overlay
     until they release it. No audio_url yet means it is still generating. */
  | "held";

export interface VoiceSettings {
  stability: number;
  similarity_boost: number;
  style: number;
  speed: number;
  use_speaker_boost: boolean;
}

export interface Row {
  id: number;
  created_at: number;
  user: string;
  text: string;
  chars: number;
  voice_id: string;
  voice_name: string;
  model_id: string;
  settings: Partial<VoiceSettings>;
  status: Status;
  error: string | null;
  audio_file: string | null;
  audio_url: string | null;
  bytes: number;
  latency_ms: number;
  played_at: number | null;
  /* Which shared preset said it, if any — the number that also appears in that
     character's overlay URL. */
  preset: number | null;
  /* On the soundboard, and the name on its pad if it was given one. */
  starred: boolean;
  label: string | null;
}

export interface Limits {
  max_chars?: number;
  cooldown_seconds?: number;
  daily_char_budget?: number;
  max_queue?: number;
  history_retention_days?: number;
  min_credits_remaining?: number;
}

export interface Quota {
  daily_budget: number;
  used_today: number;
  remaining: number | null;
  cooldown_seconds: number;
  cooldown_left: number;
  muted: boolean;
}

export interface Stats {
  total: number;
  total_chars: number;
  failed: number;
  today: number;
  today_chars: number;
  avg_latency_ms: number;
  /* Characters spent per day, oldest first, always the full window. */
  daily_chars: number[];
}

export interface Subscription {
  tier: string | null;
  used: number;
  limit: number;
  remaining: number;
  resets_at: number | null;
}

export type TuberFrame = string;

export interface FrameState {
  id: string;
  custom: boolean;
  version: string;
}

export interface TuberStep {
  frame: string;
  hold_ms: number;
}

export interface TuberIdle {
  name: string;
  steps: TuberStep[];
  step_ms: number;
  weight: number;
  while_talking: boolean;
}

export interface TuberAnimation {
  rest_frame: string;
  talk_mode: "cycle" | "loudness";
  talk_frames: TuberStep[];
  talk_frame_ms: number;
  talk_hold_ms: number;
  idle_min_ms: number;
  idle_max_ms: number;
  idles: TuberIdle[];
  bounce: number;
  /* Sway while talking, a squash on loud syllables, and a slow breath at rest.
     All 0 by default, which is how every character looked before they existed. */
  tilt: number;
  pulse: number;
  breathe: number;
}

/* Which browser source something is about: null is the main overlay — the URL
   that was in OBS before presets existed — and a number is the preset whose
   `?stage=` says so. */
export type Stage = number | null;

export interface TuberState {
  stage: Stage;
  size: number;
  side: "left" | "center" | "right";
  /* Nudge away from that side, in vw. Two characters anchored to the same edge
     would otherwise stand inside each other. */
  offset: number;
  frames: FrameState[];
  missing?: string[];
  animation?: TuberAnimation;
  /* Legacy/convenience fields if provided */
  custom?: Record<string, boolean>;
  version?: string;
}

/* How one stage's overlay is configured. `own_source` is the switch that gives
   a character its own OBS browser source — and therefore its own URL, its own
   volume and its own place on the canvas. */
export interface PresetOverlay {
  own_source: boolean;
  volume: number;
  tuber_size: number;
  tuber_side: "left" | "center" | "right";
  tuber_offset: number;
  animation?: TuberAnimation;
}

/* A character the instance can speak as: shared by every panel, edited by an
   admin, and optionally locked so only an admin may use it.

   `id` is the per-tenant number. It is never reused, which is what lets a
   preset's overlay URL sit in an OBS scene collection untouched. */
export interface Preset {
  id: number;
  name: string;
  voice_id: string;
  voice_name: string;
  model_id: string;
  settings: VoiceSettings;
  overlay: PresetOverlay;
  locked: boolean;
  updated_at: number;
  /* Admin-only. A non-admin is sent the preset without them, because the URL
     embeds the overlay token and the rest is editor furniture.

     Whether OBS is connected to this character is deliberately *not* here: it
     changes without anything writing the row, so it arrives on the `presence`
     frame with every other stage and is joined on the stage number. */
  tuber?: TuberState;
  overlay_url?: string | null;
}

export interface StagePresence {
  connected: number;
  seen_at: number | null;
}

export interface StageStatus extends StagePresence {
  stage: Stage;
}

/* One browser source and what it is playing. A list, because a character per
   source means several can be talking at once. */
export interface Playing {
  stage: Stage;
  row: Row;
}

export interface Mute {
  user: string;
  muted_at: number;
  reason: string | null;
}

/* One name that has spoken on this instance, with what it has spent. There is
   no users table behind this — a name is whatever somebody typed at the login
   box — so it is an aggregate over the request log. */
export interface Person {
  user: string;
  messages: number;
  chars: number;
  messages_today: number;
  chars_today: number;
  failed: number;
  last_at: number;
  muted: boolean;
  muted_reason: string | null;
  online: boolean;
}

/* The tenant's own configuration — what config.yaml used to be, now a row the
   client edits from their own panel. Every field is required here because the
   server always sends a complete document, filled in from its defaults. */
export interface TenantSettings {
  elevenlabs: {
    default_model: string;
    default_voice_id: string;
    output_format: string;
    voice_allowlist: string[];
  };
  limits: {
    max_chars: number;
    cooldown_seconds: number;
    daily_char_budget: number;
    max_queue: number;
    history_retention_days: number;
    min_credits_remaining: number;
  };
  moderation: {
    blocklist: string[];
    locked: boolean;
    presets_only: boolean;
  };
  overlay: {
    volume: number;
    tuber_size: number;
    tuber_side: "left" | "center" | "right";
    animation?: TuberAnimation;
  };
  /* What the voice is handed in place of what was typed. */
  pronunciation: Pronunciation[];
  appearance: Appearance;
}

export interface Pronunciation {
  from: string;
  to: string;
}

/* The fixed palette the server accepts; anything else is folded back to blue. */
export const ACCENTS = ["blue", "violet", "pink", "teal", "green", "amber", "red"] as const;
export type Accent = (typeof ACCENTS)[number];

export interface Appearance {
  accent: Accent;
}

export interface TenantIdentity {
  slug: string;
  display_name: string;
}

/* Everything the panel is ever told about the stored ElevenLabs key: whether
   there is one and how old it is. The key itself is write-only. */
export interface KeyStatus {
  set: boolean;
  updated_at: number | null;
}

export interface Bootstrap {
  me: Me;
  tenant: TenantIdentity;
  /* Only sent to a tenant admin; null for everyone else. */
  settings: TenantSettings | null;
  elevenlabs_key: KeyStatus | null;
  voices: Voice[];
  models: Model[];
  subscription: Subscription | null;
  defaults: { voice_id: string; model_id: string; output_format: string };
  limits: Limits;
  /* Filtered for whoever asked: a locked preset is simply absent for a
     non-admin, rather than sent with a flag the panel is trusted to honour. */
  presets: Preset[];
  presets_only: boolean;
  locked: boolean;
  quota: Quota;
  history: Row[];
  /* Every starred clip, however old — not a slice of `history`. */
  soundboard: Row[];
  appearance: Appearance;
  stats: Stats;
  now_playing: Playing[];
  overlay_url: string | null;
  overlays_connected: number;
  /* Unix seconds an overlay was last heard from, or null if none has been on
     this boot. A connection count alone cannot tell a working browser source
     from one whose machine went to sleep with the socket still open. */
  overlay_seen_at: number | null;
  /* Per browser source. "One overlay is connected" stops being a useful answer
     once a streamer is running three of them and one has quietly dropped. */
  stages: StageStatus[];
  volume: number;
  tuber: TuberState;
  online: string[];
  warnings: string[];
}

/* The `{event, data}` frames the panel socket pushes. */
export type SocketEvent =
  | { event: "request.new"; data: Row }
  | { event: "request.update"; data: Row }
  | { event: "request.delete"; data: { id: number } }
  | { event: "history.clear"; data: Record<string, never> }
  | { event: "now_playing"; data: Playing[] }
  | { event: "stats"; data: Stats }
  | { event: "subscription"; data: Subscription }
  | { event: "locked"; data: { locked: boolean; presets_only: boolean } }
  /* The shared list changed. Deliberately empty: the hub broadcasts to a
     tenant, not to a person, so each panel refetches and gets its own
     filtered answer rather than being handed the admin-only rows. */
  | { event: "presets.changed"; data: Record<string, never> }
  | {
      event: "presence";
      data: {
        online: string[];
        overlays: number;
        overlay_seen_at: number | null;
        stages: StageStatus[];
      };
    }
  | { event: "volume"; data: { volume: number; stage: Stage } }
  | { event: "tuber"; data: TuberState }
  | { event: "toast"; data: { text: string } }
  | { event: "appearance"; data: Appearance };
